import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PmNotifications = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setItems(data.notifications || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load notifications');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      load();
    } catch { /* noop */ }
  };

  const markAll = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      load();
      toast.success('All notifications marked as read');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const filtered = filter === 'unread' ? items.filter((n) => !n.read) : items;
  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <div data-testid="pm-notifications-page" className="space-y-6 max-w-3xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">NOTIFICATIONS</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Inbox</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">{unreadCount} unread · {items.length} total</p>
        </div>
        <button
          onClick={markAll}
          disabled={unreadCount === 0}
          data-testid="pm-notifs-mark-all"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#141414] border border-[#262626] text-[#A3A3A3] hover:text-white disabled:opacity-40 text-sm"
        >
          <CheckCheck className="w-4 h-4" /> Mark all read
        </button>
      </div>

      <div className="flex gap-2 text-xs">
        {['all', 'unread'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`pm-notifs-filter-${f}`}
            className={`px-3 py-1.5 rounded-md border ${filter === f ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}
          >
            {f === 'all' ? 'All' : `Unread (${unreadCount})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#A3A3A3]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div data-testid="pm-notifs-empty" className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center">
          <Inbox className="w-8 h-8 text-[#525252] mx-auto mb-2" />
          <p className="text-[#A3A3A3] text-sm">{filter === 'unread' ? 'No unread notifications.' : 'No notifications yet.'}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((n) => {
            const nid = n.id || n._id;
            const Row = (
              <div
                data-testid={`pm-notif-${nid}`}
                className={`flex items-start gap-3 p-4 rounded-md border ${n.read ? 'bg-[#141414] border-[#262626]' : 'bg-[#D4AF37]/5 border-[#D4AF37]/40'}`}
              >
                <Bell className={`w-4 h-4 mt-0.5 shrink-0 ${n.read ? 'text-[#525252]' : 'text-[#D4AF37]'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${n.read ? 'text-white' : 'text-[#D4AF37]'}`}>{n.title}</p>
                  {n.message && <p className="text-[#A3A3A3] text-sm mt-0.5">{n.message}</p>}
                  <p className="text-[#525252] text-[10px] uppercase tracking-wider mt-1">
                    {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                  </p>
                </div>
                {!n.read && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); markRead(nid); }}
                    className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:text-[#F3E5AB] px-2 py-1 rounded"
                  >
                    Mark read
                  </button>
                )}
              </div>
            );
            return (
              <li key={nid}>
                {n.link ? (
                  <Link to={n.link} onClick={() => !n.read && markRead(nid)} className="block">{Row}</Link>
                ) : Row}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PmNotifications;
